import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { api, ApiError } from '../api';
import { useAuth } from '../auth';
import { TopBar } from '../components/Layout';
import { ErrorBox, OfflineBanner } from '../components/ui';
import { formatPhone, normalizePhone } from '../format';
import { useT } from '../i18n';
import type { AppMessageKey } from '../messages';

function returnPath(state: unknown): string {
  const from = (state as { from?: unknown } | null)?.from;
  return typeof from === 'string' && from.startsWith('/') && !from.startsWith('/login') ? from : '/';
}

export function LoginPage() {
  const t = useT();
  const { status, signIn } = useAuth();
  const location = useLocation();
  const [step, setStep] = useState<'password' | 'phone' | 'code'>('password');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);
  const [formError, setFormError] = useState<AppMessageKey | null>(null);

  const request = useMutation({
    mutationFn: (p: string) => api.requestCode(p),
    onSuccess: (res, p) => {
      setPhone(p);
      setDevCode(res.devCode ?? null);
      setCode('');
      setStep('code');
    },
  });
  const verify = useMutation({
    mutationFn: (v: { phone: string; code: string }) => api.verifyCode(v.phone, v.code),
    onSuccess: (me) => signIn(me), // status flips to signedIn and the <Navigate> below takes over
  });
  const withPassword = useMutation({
    mutationFn: (v: { login: string; password: string }) => api.passwordLogin(v),
    onSuccess: (me) => signIn(me),
  });
  const withPasskey = useMutation({
    mutationFn: async () => {
      const { challengeId, options } = await api.passkeyLoginOptions();
      const response = await startAuthentication({ optionsJSON: options as unknown as Parameters<typeof startAuthentication>[0]['optionsJSON'] });
      return api.passkeyLoginVerify(challengeId, response as unknown as Record<string, unknown>);
    },
    onSuccess: (me) => signIn(me),
  });
  const passkeysHere = typeof window !== 'undefined' && browserSupportsWebAuthn();

  function submitPassword(e: FormEvent) {
    e.preventDefault();
    if (login.trim().length < 2 || !password) return setFormError('loginNeeded');
    setFormError(null);
    withPassword.mutate({ login: login.trim(), password });
  }

  if (status === 'signedIn') return <Navigate to={returnPath(location.state)} replace />;

  function submitPhone(e: FormEvent) {
    e.preventDefault();
    const p = normalizePhone(phoneInput);
    if (!p) return setFormError('phoneInvalid');
    setFormError(null);
    request.mutate(p);
  }

  function submitCode(e: FormEvent) {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return setFormError('codeInvalid');
    setFormError(null);
    verify.mutate({ phone, code });
  }

  const verifyRejected =
    verify.error instanceof ApiError && [400, 401, 403, 404, 422].includes(verify.error.status);
  const passwordRejected = withPassword.error instanceof ApiError && [400, 401].includes(withPassword.error.status);

  return (
    <div className="app">
      <div className="top">
        <TopBar />
        <OfflineBanner />
      </div>
      <main className="content narrow stack">
        <h1>{t('signIn')}</h1>
        {step === 'password' ? (
          <form className="stack" onSubmit={submitPassword} noValidate>
            <label className="field">
              <span>{t('loginLabel')}</span>
              <input type="text" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={login} onChange={(e) => setLogin(e.target.value)} />
            </label>
            <label className="field">
              <span>{t('password')}</span>
              <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </label>
            {formError ? <p className="field-error" role="alert">{t(formError)}</p> : null}
            {passwordRejected ? (
              <p className="field-error" role="alert">{t('wrongLogin')}</p>
            ) : withPassword.error ? (
              <ErrorBox error={withPassword.error} />
            ) : null}
            <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={withPassword.isPending}>
              {withPassword.isPending ? t('sending') : t('signIn')}
            </button>
            {passkeysHere ? (
              <button type="button" className="btn btn-secondary btn-lg btn-block" disabled={withPasskey.isPending} onClick={() => withPasskey.mutate()}>
                {t('signInWithPasskey')}
              </button>
            ) : null}
            {withPasskey.error && !(withPasskey.error instanceof DOMException && withPasskey.error.name === 'NotAllowedError') ? (
              withPasskey.error instanceof ApiError && withPasskey.error.status === 401 ? <p className="field-error" role="alert">{t('passkeyUnknown')}</p> : <ErrorBox error={withPasskey.error} />
            ) : null}
            <button type="button" className="btn btn-ghost btn-block" onClick={() => { setStep('phone'); setFormError(null); }}>
              {t('signInWithCode')}
            </button>
          </form>
        ) : step === 'phone' ? (
          <form className="stack" onSubmit={submitPhone} noValidate>
            <label className="field">
              <span>{t('phone')}</span>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                placeholder="(505) 555-1234"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
              />
            </label>
            {formError ? <p className="field-error" role="alert">{t(formError)}</p> : null}
            {request.error ? <ErrorBox error={request.error} /> : null}
            <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={request.isPending}>
              {request.isPending ? t('sending') : t('sendCode')}
            </button>
            <button type="button" className="btn btn-ghost btn-block" onClick={() => { setStep('password'); setFormError(null); }}>
              {t('signInWithPassword')}
            </button>
          </form>
        ) : (
          <form className="stack" onSubmit={submitCode} noValidate>
            <p>
              {t('codeSent')} <strong>{formatPhone(phone)}</strong>
            </p>
            {devCode ? (
              <p className="devcode">
                {t('testCode')}: <strong data-testid="dev-code">{devCode}</strong>
              </p>
            ) : null}
            <label className="field">
              <span>{t('code')}</span>
              <input
                className="code-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </label>
            {formError ? <p className="field-error" role="alert">{t(formError)}</p> : null}
            {verifyRejected ? (
              <p className="field-error" role="alert">{t('codeInvalid')}</p>
            ) : verify.error ? (
              <ErrorBox error={verify.error} />
            ) : null}
            {request.error ? <ErrorBox error={request.error} /> : null}
            <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={verify.isPending}>
              {verify.isPending ? t('sending') : t('verify')}
            </button>
            <div className="quick-grid">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={request.isPending}
                onClick={() => request.mutate(phone)}
              >
                {t('sendCode')}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setStep('phone');
                  setDevCode(null);
                  setFormError(null);
                  verify.reset();
                }}
              >
                {t('otherPhone')}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
